import { Check, ChevronRight, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'

import { t } from '../../copy'
import { Button } from '../../components/button'
import { cn } from '../../components/cn'
import { Tag } from '../../components/tag'
import {
  useCreateModelProfile,
  useDiscoverModels,
  useModelConnections,
  useModelProfiles,
  normalizeDiscoveredModels,
  useModelProviders,
  useProbeModelProfile,
  type DiscoveredModel,
} from './model-library'

export type BindableModel = {
  profileId: string
  displayName: string
  modelId: string
  provider: string
  connectionId: string
  connectionName: string
}

type Props = {
  options: readonly BindableModel[]
  value: string
  scope: 'account' | 'project'
  projectId?: string
  disabled?: boolean
  onSelect: (profileId: string, option: BindableModel) => void
  onManageModels: (providerId?: string) => void
}

const selectClass =
  'min-h-11 w-full rounded-npc-button border border-npc-border bg-npc-panel px-npc-gap-lg text-npc-body text-npc-ink outline-none focus:border-npc-green disabled:cursor-not-allowed disabled:text-npc-ink-softer'

/**
 * A binding always ends at a verified Model Profile. The second tab is only a
 * shorter way to create and test that profile from an existing connection; it
 * never stores a transient provider/model pair in an Agent or system module.
 */
export function ModelSourcePicker({
  options,
  value,
  scope,
  projectId,
  disabled,
  onSelect,
  onManageModels,
}: Props) {
  const [mode, setMode] = useState<'profiles' | 'connections'>('profiles')
  const [providerId, setProviderId] = useState('')
  const [connectionId, setConnectionId] = useState('')
  const [runtimeModels, setRuntimeModels] = useState<DiscoveredModel[]>([])
  const [busyModel, setBusyModel] = useState('')
  const [error, setError] = useState('')
  const providers = useModelProviders()
  const connections = useModelConnections()
  const libraryProfiles = useModelProfiles(scope === 'project' ? projectId : undefined)
  const discover = useDiscoverModels()
  const createProfile = useCreateModelProfile()
  const probeProfile = useProbeModelProfile()

  const providerById = useMemo(
    () => new Map((providers.data?.providers ?? []).map((provider) => [provider.id, provider])),
    [providers.data?.providers],
  )
  const configuredConnections = (connections.data?.connections ?? []).filter(
    (connection) =>
      connection.credential_present &&
      connection.status !== 'disabled' &&
      providerById.get(connection.provider)?.adapter_state !== 'requires_adapter',
  )
  const availableProviders = (providers.data?.providers ?? []).filter(
    (provider) => provider.id !== 'custom',
  )
  const effectiveProviderId =
    providerId ||
    (configuredConnections.length === 1 ? (configuredConnections[0]?.provider ?? '') : '')
  const selectedProvider = providerById.get(effectiveProviderId)
  const providerConnections = configuredConnections.filter(
    (connection) => connection.provider === effectiveProviderId,
  )
  const effectiveConnectionId =
    connectionId || (providerConnections.length === 1 ? (providerConnections[0]?.id ?? '') : '')
  const selectedConnection = providerConnections.find(
    (connection) => connection.id === effectiveConnectionId,
  )
  const catalogModels = selectedProvider?.catalog_models ?? []
  const supportedModels = selectedProvider?.can_discover
    ? runtimeModels
    : catalogModels.map((model_id) => ({ model_id }))

  const chooseProvider = (nextId: string) => {
    setProviderId(nextId)
    setConnectionId('')
    setRuntimeModels([])
    setError('')
  }

  const chooseConnection = (nextId: string) => {
    setConnectionId(nextId)
    setRuntimeModels([])
    setError('')
  }

  const readRuntimeModels = async () => {
    if (!selectedConnection || !selectedProvider?.can_discover) return
    setError('')
    try {
      const result = await discover.mutateAsync(selectedConnection.id)
      const models = normalizeDiscoveredModels(result.models)
      setRuntimeModels(models)
      if (models.length === 0) setError(t('model_configuration.discovery_empty'))
    } catch {
      setError(t('model_configuration.connection_failed'))
    }
  }

  const addAndSelect = async (model: DiscoveredModel) => {
    if (!selectedConnection) return
    const modelId = model.model_id
    setError('')
    setBusyModel(modelId)
    try {
      const direct = options.find(
        (option) => sameConnection(option, selectedConnection) && option.modelId === modelId,
      )
      if (direct) {
        onSelect(direct.profileId, direct)
        return
      }

      const visibleProfiles = libraryProfiles.data?.profiles ?? []
      let profile = visibleProfiles.find(
        (candidate) =>
          candidate.connection_id === selectedConnection.id &&
          candidate.model_id === modelId &&
          (scope === 'account' ? !candidate.project_id : candidate.project_id === projectId),
      )
      if (!profile) {
        const created = await createProfile.mutateAsync({
          connection_id: selectedConnection.id,
          ...(scope === 'project' && projectId ? { project_id: projectId } : {}),
          display_name: modelId,
          model_id: modelId,
          ...(model.max_input_tokens ? { max_input_tokens: model.max_input_tokens } : {}),
          ...(model.max_output_tokens ? { max_output_tokens: model.max_output_tokens } : {}),
          ...(model.supports_vision !== undefined
            ? { supports_vision: model.supports_vision }
            : {}),
          ...(model.supports_reasoning !== undefined
            ? { supports_reasoning: model.supports_reasoning }
            : {}),
        })
        profile = created.profile
      }
      const tested = await probeProfile.mutateAsync(profile.id)
      if (tested.profile.status !== 'available') {
        setError(failureText(tested.profile.last_error_code))
        return
      }
      onSelect(tested.profile.id, {
        profileId: tested.profile.id,
        displayName: tested.profile.display_name,
        modelId: tested.profile.model_id,
        provider: tested.profile.provider,
        connectionId: tested.profile.connection_id,
        connectionName: tested.profile.connection_name,
      })
    } catch {
      setError(t('model_configuration.model_saved_test_failed'))
    } finally {
      setBusyModel('')
    }
  }

  return (
    <div className="grid min-w-0 gap-npc-gap-sm">
      <div
        className="grid grid-cols-2 rounded-npc-button bg-npc-surface p-npc-gap-xs"
        role="tablist"
        aria-label={t('model_configuration.binding_picker_mode')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'profiles'}
          onClick={() => setMode('profiles')}
          className={tabClass(mode === 'profiles')}
        >
          {t('model_configuration.binding_picker_profiles')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'connections'}
          onClick={() => setMode('connections')}
          className={tabClass(mode === 'connections')}
        >
          {t('model_configuration.binding_picker_connections')}
        </button>
      </div>

      {mode === 'profiles' ? (
        <div className="max-h-72 overflow-y-auto rounded-npc-card border border-npc-border bg-npc-panel p-npc-gap-sm">
          {options.length === 0 ? (
            <p className="m-0 p-npc-gap-sm text-npc-caption text-npc-ink-soft">
              {t('model_configuration.binding_picker_empty_profiles')}
            </p>
          ) : (
            options.map((option) => {
              const active = option.profileId === value
              return (
                <button
                  key={option.profileId}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={disabled}
                  onClick={() => onSelect(option.profileId, option)}
                  className={cn(
                    'flex w-full items-start justify-between gap-npc-gap rounded-npc-button px-npc-gap-lg py-npc-gap-lg text-left hover:bg-npc-surface disabled:opacity-50',
                    active && 'bg-npc-green-bg',
                  )}
                >
                  <ModelIdentity
                    option={option}
                    providerName={providerById.get(option.provider)?.name}
                  />
                  {active ? (
                    <Check size={16} aria-hidden="true" className="shrink-0 text-npc-green-deep" />
                  ) : null}
                </button>
              )
            })
          )}
        </div>
      ) : (
        <div className="grid gap-npc-gap-sm rounded-npc-card border border-npc-border bg-npc-panel p-npc-card-sm">
          <label className="grid gap-npc-gap-xs text-npc-caption font-semibold text-npc-ink-medium">
            {t('model_configuration.binding_picker_provider_label')}
            <select
              className={selectClass}
              value={effectiveProviderId}
              disabled={disabled || providers.isPending}
              onChange={(event) => chooseProvider(event.target.value)}
            >
              <option value="">
                {t('model_configuration.binding_picker_provider_placeholder')}
              </option>
              {availableProviders.map((provider) => (
                <option
                  key={provider.id}
                  value={provider.id}
                  disabled={provider.adapter_state === 'requires_adapter' || !provider.can_test}
                >
                  {provider.name}
                  {provider.adapter_state === 'requires_adapter'
                    ? ` · ${t('model_configuration.ui_062')}`
                    : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-npc-gap-xs text-npc-caption font-semibold text-npc-ink-medium">
            {t('model_configuration.binding_picker_connection_label')}
            <select
              className={selectClass}
              value={effectiveConnectionId}
              disabled={
                disabled ||
                !selectedProvider ||
                providerConnections.length === 0 ||
                connections.isPending ||
                providers.isPending
              }
              onChange={(event) => chooseConnection(event.target.value)}
            >
              <option value="">
                {t('model_configuration.binding_picker_connection_placeholder')}
              </option>
              {providerConnections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name} ·{' '}
                  {providerById.get(connection.provider)?.name ?? connection.provider}
                </option>
              ))}
            </select>
          </label>

          {selectedProvider && providerConnections.length === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-npc-gap-sm border-y border-npc-border py-npc-gap-sm">
              <p className="m-0 text-npc-caption text-npc-ink-soft">
                {t('model_configuration.binding_picker_provider_unconfigured', {
                  provider: selectedProvider.name,
                })}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onManageModels(selectedProvider.id)}
              >
                {t('model_configuration.binding_picker_configure_provider')}
              </Button>
            </div>
          ) : !selectedProvider && configuredConnections.length === 0 ? (
            <p className="m-0 text-npc-caption text-npc-ink-soft">
              {t('model_configuration.binding_picker_empty_connections')}
            </p>
          ) : null}

          {selectedConnection && selectedProvider?.can_discover && runtimeModels.length === 0 ? (
            <Button
              type="button"
              variant="secondary"
              disabled={discover.isPending}
              onClick={() => void readRuntimeModels()}
            >
              <RefreshCw size={14} aria-hidden="true" />
              {t('model_configuration.fetch_models')}
            </Button>
          ) : null}

          {selectedConnection &&
          selectedProvider &&
          !selectedProvider.can_discover &&
          catalogModels.length === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-npc-gap-sm border-y border-npc-border py-npc-gap-sm">
              <p className="m-0 text-npc-caption text-npc-ink-soft">
                {t('model_configuration.discovery_not_supported')}
              </p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onManageModels(selectedProvider.id)}
              >
                {t('model_configuration.ui_226')}
              </Button>
            </div>
          ) : null}

          {selectedConnection && supportedModels.length > 0 ? (
            <div
              className="grid max-h-72 gap-npc-gap-xs overflow-y-auto"
              aria-label={t('model_configuration.binding_picker_supported_models')}
            >
              <p className="m-0 text-npc-meta text-npc-ink-soft">
                {selectedProvider?.can_discover
                  ? t('model_configuration.binding_picker_runtime_hint')
                  : t('model_configuration.binding_picker_catalog_hint')}
              </p>
              {supportedModels.map((model) => {
                const modelId = model.model_id
                const existing = options.find(
                  (option) =>
                    sameConnection(option, selectedConnection) && option.modelId === modelId,
                )
                return (
                  <button
                    key={modelId}
                    type="button"
                    disabled={disabled || Boolean(busyModel)}
                    onClick={() => void addAndSelect(model)}
                    className="grid min-h-11 grid-cols-[minmax(0,1fr)_auto] items-center gap-npc-gap rounded-npc-button px-npc-gap-lg py-npc-gap-sm text-left text-npc-caption text-npc-ink hover:bg-npc-surface disabled:opacity-50 max-[520px]:items-start"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{modelId}</span>
                      <span className="block truncate text-npc-meta text-npc-ink-soft">
                        {selectedProvider?.name ?? selectedConnection.provider} ·{' '}
                        {selectedConnection.name}
                      </span>
                    </span>
                    <span className="flex max-w-28 shrink-0 items-center justify-end gap-npc-gap-xs text-right text-npc-meta leading-snug text-npc-green-deep max-[520px]:max-w-20">
                      {busyModel === modelId
                        ? t('model_configuration.binding_picker_testing')
                        : existing
                          ? t('model_configuration.binding_picker_select_existing')
                          : t('model_configuration.binding_picker_add_select')}
                      <ChevronRight size={14} aria-hidden="true" />
                    </span>
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>
      )}

      {error ? (
        <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => onManageModels()}
        className="justify-self-start border-0 bg-transparent p-0 text-npc-caption font-semibold text-npc-blue-ink hover:underline"
      >
        {t('model_configuration.ui_226')}
      </button>
    </div>
  )
}

function ModelIdentity({ option, providerName }: { option: BindableModel; providerName?: string }) {
  return (
    <span className="min-w-0">
      <span className="flex min-w-0 flex-wrap items-center gap-npc-gap-xs">
        <span className="truncate text-npc-caption font-semibold text-npc-ink">
          {option.displayName}
        </span>
        <Tag tone="neutral">{providerName ?? option.provider}</Tag>
      </span>
      <span className="block truncate text-npc-meta text-npc-ink-soft">
        {option.modelId} · {option.connectionName}
      </span>
    </span>
  )
}

function tabClass(active: boolean) {
  return cn(
    'min-h-10 rounded-npc-chip px-npc-grid text-npc-caption font-semibold',
    active
      ? 'border border-npc-border-hover bg-npc-surface-warm text-npc-ink'
      : 'border border-transparent text-npc-ink-soft',
  )
}

function failureText(code: string | null | undefined) {
  if (code === 'authentication_failed') return t('model_configuration.failure_auth')
  if (code === 'model_not_found') return t('model_configuration.failure_model_not_found')
  if (code === 'network_error') return t('model_configuration.failure_network')
  if (code === 'rate_limited') return t('model_configuration.failure_rate_limited')
  if (code === 'quota_exhausted') return t('model_configuration.failure_quota_exhausted')
  return t('model_configuration.model_saved_test_failed')
}

function sameConnection(
  option: BindableModel,
  connection: { id: string; name: string; provider: string },
) {
  return option.connectionId
    ? option.connectionId === connection.id
    : option.connectionName === connection.name && option.provider === connection.provider
}
